import type { PushType } from '../../../domain/push-type.enum'
import type {
  PremiumAchPull,
  PremiumAchPullDetailed,
  PremiumAchPullInvoice,
  PremiumAchPullStatus,
  PremiumGroupInvoice,
  PremiumGroupInvoiceLine,
  PremiumInvoiceStatus
} from '../types'
import type { TableColumnsType } from 'antd'

import {
  CaretDownOutlined,
  CloseOutlined,
  RightOutlined,
  SearchOutlined,
  SyncOutlined
} from '@ant-design/icons'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Dropdown, Input, Space, Table, Tag } from 'antd'
import cx from 'classnames'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import $api from '../../../API'
import { mapper as PushTypeNameMapper } from '../../../domain/push-type.enum'
import { EmptyCellValue } from '../components/empty-cell-value'
import { ExpandIcon } from '../components/expand-icon'
import { InvoiceLines } from '../components/invoice-lines'
import { currency, duration } from '../formatters'
import { baseCellClassName, baseHeaderCellClassName } from '../groups'
import { PremiumInvoiceStatusMapper } from '../types'

import { PremiumPullFooter } from './premium-pull-footer'
import { PullStatusSelector } from './pull-status-selector'

type QueryContextKey = [
  'batch'
  // ListState['sort'],
  // ListState['pagination'],
  // ListState['searchParams']
]

interface QueryData {
  pull: PremiumAchPull
  invoices: PremiumAchPullInvoice[]
}

export const PremiumAchPullDetailPage = (): JSX.Element => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [expandedRow, setExpandedRow] = useState<string>('')
  const searchQuery = ''

  /* columns */
  const columns = useMemo<TableColumnsType<PremiumAchPullInvoice>>(
    () => [
      {
        title: 'Group #',
        key: 'group_number',
        dataIndex: 'group_number',
        sorter: true,
        showSorterTooltip: false,
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Group name',
        key: 'group_name',
        dataIndex: 'group_name',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Payment type',
        key: 'group_premium_funding_type',
        dataIndex: 'group_premium_funding_type',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) }),
        render(value?: PushType) {
          if (value === undefined) {
            return <EmptyCellValue />
          }
          return PushTypeNameMapper[value]
        }
      },
      {
        title: 'Invoice #',
        key: 'number',
        dataIndex: 'number',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) })
      },
      {
        title: 'Amount',
        key: 'amount',
        dataIndex: 'amount',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) }),
        render(value: number | null | undefined) {
          if (typeof value !== 'number') {
            return <EmptyCellValue />
          }
          return <span>{currency(value)}</span>
        }
      },
      {
        title: 'Account',
        key: 'group_premium_account_number',
        dataIndex: 'group_premium_account_number',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) }),
        render: (value: string | null) => value ?? <EmptyCellValue />
      },
      {
        title: 'Routing number',
        key: 'group_premium_routing_number',
        dataIndex: 'group_premium_routing_number',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) }),
        render: (value: string | null) => value ?? <EmptyCellValue />
      },
      {
        title: 'Status',
        dataIndex: 'status',
        onCell: () => ({ className: cx(baseCellClassName) }),
        onHeaderCell: () => ({ className: cx(baseHeaderCellClassName) }),
        render: (value: PremiumInvoiceStatus) => (
          <Tag
            color={PremiumInvoiceStatusMapper[value].color}
            title={PremiumInvoiceStatusMapper[value].name}
            className='font-lato size-xs'
          >
            {PremiumInvoiceStatusMapper[value].name}
          </Tag>
        )
      }
    ],
    []
  )

  /* selection */

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<PremiumAchPullInvoice[]>([])

  const onSelectChange = (
    newSelectedRowKeys: React.Key[],
    newSelectedRows: PremiumAchPullInvoice[]
  ): void => {
    setSelectedRowKeys(newSelectedRowKeys)
    setSelectedRows(newSelectedRows)
  }

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange
  }

  /* query */

  const query = useQuery<unknown, unknown, QueryData, QueryContextKey>({
    queryKey: [
      'batch'
      // state.sort,
      // state.pagination,
      // { ...state.searchParams, query: debouncedQuery }
    ],

    async queryFn() {
      const { data } = await $api.get<{ result: PremiumAchPullDetailed }>(`/batch?batchId=${id}`)

      const pull = data.result
      const invoices = data.result.groups.flatMap(g =>
        g.invoices.map(i => ({
          ...i,
          amount: i.lines.reduce(
            (acc: number, line: PremiumGroupInvoiceLine) => acc + line.amount,
            0
          ),
          group_name: g.name,
          group_number: g.number,
          group_premium_funding_type: g.premium_funding_type,
          group_premium_account_number: g.premium_account_number,
          group_premium_routing_number: g.premium_routing_number
        }))
      )

      return {
        pull,
        invoices
      }
    },
    keepPreviousData: false
  })

  const mutation = useMutation({
    mutationFn: async (status: PremiumAchPullStatus) => {
      try {
        const response = await $api.post('/batch/status', {
          headers: { 'Content-Type': 'application/json' },
          body: { id, status }
        })
        return response.data
      } catch {
        throw new Error('Failed to update status')
      }
    },
    onSuccess: async () => {
      await query.refetch()
    }
  })

  useEffect(() => {
    // todo - review approach

    query.refetch()
  }, [id, query])

  /* event handlers */

  const onTitleClick = useCallback(() => {
    navigate('/premium/pulls')
  }, [navigate])

  const onStatusChange = useCallback(
    (status: PremiumAchPullStatus) => {
      mutation.mutate(status)
    },
    [mutation]
  )

  const clearSelection = useCallback(() => {
    setSelectedRowKeys([])
    setSelectedRows([])
  }, [])

  const onExcludeInvoices = useCallback(() => {
    console.info('onExcludeInvoices')
    clearSelection()
    void query.refetch()
  }, [query, clearSelection])

  const handleRowExpand = useCallback((expanded: boolean, record: PremiumAchPullInvoice) => {
    setExpandedRow(expanded ? record.id : '')
  }, [])

  return (
    <>
      <div className='flex flex-col gap-4 p-6 font-normal rounded-lg font-lato bg-lucent-background text-lucent-text'>
        <div className='flex gap-3 justify-between items-center'>
          <h1 className='text-2xl cursor-pointer' onClick={onTitleClick}>
            Premium ACH Pulls
          </h1>
          {!query.isFetching && query.data?.pull !== undefined && (
            <>
              <RightOutlined />
              <h1>{query.data.pull.name}</h1>
              <PullStatusSelector
                status={query.data.pull.status}
                onClick={onStatusChange}
                loading={mutation.isLoading}
                className='ml-auto'
              />
            </>
          )}
        </div>
        <div className='flex flex-col rounded-lg border'>
          <div className='flex flex-col'>
            <div className='flex flex-row gap-2 items-center p-4'>
              <div className='flex-grow'>
                <Input
                  placeholder='Search invoices'
                  className='font-lato text-sm py-1 bg-gray-50 [&_input]:bg-transparent rounded [&_input]:placeholder:text-gray-400'
                  prefix={<SearchOutlined className='text-gray-500' />}
                  suffix={
                    searchQuery.length > 0 ? (
                      <button
                        type='button'
                        className='flex justify-center items-center p-2 -m-2 border'
                        // onClick={() => {
                        //   dispatch({
                        //     type: 'set-query',
                        //     payload: ''
                        //   })
                        // }}
                      >
                        <CloseOutlined size={16} className='text-gray-500' />
                      </button>
                    ) : undefined
                  }
                  // value={state.searchParams.query ?? ''}
                  // onChange={event => {
                  //   dispatch({
                  //     type: 'set-query',
                  //     payload: event.target.value
                  //   })
                  // }}
                />
              </div>
              <Space>
                {/* {!isDefaultFilters && (
                  <Button
                    type='default'
                    // onClick={() => {
                    //   dispatch({
                    //     type: 'clear-filters'
                    //   })
                    // }}
                  >
                    <span className='mr-2 -translate-y-[4px]'>
                      <DeleteFilled
                        className='text-lucent-primary'
                      />
                    </span>
                    <span className='font-lato font-bold text-lucent-primary text-[10px] uppercase -translate-y-[1px]'>
                      Clear Filters
                    </span>
                  </Button>
                )} */}

                <Dropdown.Button
                  type='default'
                  // disabled={query.isFetching}
                  // onClick={() => {
                  //   void query.refetch()
                  // }}
                  icon={
                    <CaretDownOutlined className={cx('w-[12px] h-[12px] !translate-y-[2px]')} />
                  }
                  menu={{
                    items: [30, 60, 60 * 5, 60 * 30].map(seconds => ({
                      key: `refresh-${seconds}`,
                      itemIcon() {
                        return null
                      },
                      label: duration(seconds * 1000)
                    }))
                  }}
                >
                  <div className='flex items-center space-x-1.5'>
                    <SyncOutlined
                      className={cx(
                        query.isFetching && 'animate-custom-spin text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    />

                    <span
                      className={cx(
                        'font-lato font-bold text-[10px] uppercase',
                        query.isFetching && 'text-lucent-gray',
                        !query.isFetching && 'text-lucent-primary'
                      )}
                    >
                      <span>Refresh</span>
                    </span>
                  </div>
                </Dropdown.Button>
              </Space>
            </div>
          </div>
          <Table
            size='small'
            rowKey='id'
            dataSource={query.data?.invoices ?? []}
            rowClassName='cursor-pointer'
            rowSelection={rowSelection}
            loading={query.isFetching || mutation.isLoading}
            columns={columns}
            expandable={{
              expandIcon: ExpandIcon,
              showExpandColumn: true,
              expandRowByClick: true,
              expandedRowKeys: expandedRow === '' ? [] : [expandedRow],
              onExpand: handleRowExpand,
              expandedRowRender(invoice, _, __, expanded) {
                if (!expanded) {
                  return null
                }

                return (
                  <InvoiceLines
                    date={invoice.billing_date as PremiumGroupInvoice['billing_date']}
                    lines={invoice.lines as PremiumGroupInvoiceLine[]}
                  />
                )
              }
            }}
          />
        </div>
        {query.data?.pull && (
          <PremiumPullFooter
            pull={query.data.pull}
            selected={selectedRows}
            onClearSelection={clearSelection}
            onExcludeInvoices={onExcludeInvoices}
          />
        )}
      </div>
    </>
  )
}

PremiumAchPullDetailPage.displayName = 'Ach Pull Detail Page'
